import { useEffect, useState } from "react"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { Modal, ModalActions, ModalButton } from "./modal"
import { Select } from "./select"
import { Skeleton } from "./skeleton"

interface SponsorOption {
  id: string
  name: string
  code: string
  email: string
  sponsorshipRequestStatus: "new" | "contacted" | "approved" | "declined" | "closed" | null
}

interface ChildOption {
  id: string
  code: string
  firstName: string
  lastName: string
  classLevel: string
  school: string
  state: string
  status: "nominated" | "unverified" | "verified" | "sponsored" | "graduated"
}

interface SponsorMatchModalProps {
  open: boolean
  onClose: () => void
  onMatched?: () => void
  sponsorId?: string
  childId?: string
}

function MatchingOptionsSkeleton() {
  return (
    <div className="space-y-4" aria-label="Loading matching options">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-11 w-full rounded-lg" />
      </div>
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-11 w-full rounded-lg" />
      </div>
      <Skeleton className="h-12 w-full rounded-lg" />
    </div>
  )
}

export function SponsorMatchModal({ open, onClose, onMatched, sponsorId, childId }: SponsorMatchModalProps) {
  const [sponsors, setSponsors] = useState<SponsorOption[]>([])
  const [children, setChildren] = useState<ChildOption[]>([])
  const [selectedSponsorId, setSelectedSponsorId] = useState(sponsorId ?? "")
  const [selectedChildId, setSelectedChildId] = useState(childId ?? "")
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedSponsorId(sponsorId ?? "")
    setSelectedChildId(childId ?? "")
    setLoading(true)

    void Promise.all([
      client.api.admin.sponsors.$get({ query: { status: "active", limit: "100" } }),
      client.api.admin.children.$get({ query: { limit: "100" } }),
    ]).then(async ([sponsorResponse, childResponse]) => {
      const sponsorBody = await sponsorResponse.json()
      const childBody = await childResponse.json()
      if (!sponsorResponse.ok || !childResponse.ok) {
        toast.error(getErrorMessage(!sponsorResponse.ok ? sponsorBody : childBody))
        return
      }
      if ("data" in sponsorBody) {
        setSponsors(sponsorBody.data.items.filter((item) => item.sponsorshipRequestStatus === "approved"))
      }
      if ("data" in childBody) {
        const availableChildren: ChildOption[] = childBody.data.items
          .filter((item) => item.status === "verified" || item.status === "sponsored")
          .map((item) => ({
            id: item.id,
            code: item.code,
            firstName: item.firstName,
            lastName: item.lastName,
            classLevel: item.classLevel,
            school: item.school,
            state: item.state,
            status: item.status,
          }))
        if (childId && !availableChildren.some((item) => item.id === childId)) {
          const selectedChildResponse = await client.api.admin.children[":id"].$get({ param: { id: childId } })
          const selectedChildBody = await selectedChildResponse.json()
          if (selectedChildResponse.ok && "data" in selectedChildBody) {
            const selectedChild = selectedChildBody.data
            availableChildren.unshift({
              id: selectedChild.id,
              code: selectedChild.code,
              firstName: selectedChild.firstName,
              lastName: selectedChild.lastName,
              classLevel: selectedChild.classLevel,
              school: selectedChild.school,
              state: selectedChild.state,
              status: selectedChild.status,
            })
          }
        }
        setChildren(availableChildren)
      }
    }).catch(() => {
      toast.error("Failed to load matching options")
    }).finally(() => {
      setLoading(false)
    })
  }, [open, sponsorId, childId])

  const selectedSponsor = sponsors.find((sponsor) => sponsor.id === selectedSponsorId)
  const selectedChild = children.find((child) => child.id === selectedChildId)
  const selectedChildIsEligible = selectedChild?.status === "verified" || selectedChild?.status === "sponsored"
  const sponsorOptions = sponsors.map((sponsor) => `${sponsor.name} · ${sponsor.code}`)
  const childOptions = children.map((child) => `${child.firstName} ${child.lastName} · ${child.code}`)

  const handleMatch = async () => {
    if (!selectedSponsorId || !selectedChildId || !selectedChildIsEligible) return
    setSaving(true)
    try {
      const response = await client.api.admin.sponsors.match.$post({
        json: { sponsorId: selectedSponsorId, childId: selectedChildId },
      })
      const body = await response.json()
      if (!response.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success(`Matched ${selectedSponsor?.name ?? "sponsor"} with ${selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName}` : "child"}`)
      onClose()
      onMatched?.()
    } catch {
      toast.error("Failed to match sponsor and child")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Match sponsor to child"
      subtitle="Connect an active sponsor with a verified or already sponsored child."
      maxWidth="md"
    >
      {loading ? (
        <MatchingOptionsSkeleton />
      ) : (
        <div className="space-y-4">
          <Select
            label="Sponsor"
            value={selectedSponsor ? `${selectedSponsor.name} · ${selectedSponsor.code}` : ""}
            onChange={(value) => setSelectedSponsorId(sponsors.find((sponsor) => `${sponsor.name} · ${sponsor.code}` === value)?.id ?? "")}
            options={sponsorOptions}
            placeholder="Select an active sponsor"
            emptyMessage="No active sponsors available."
            searchable
            searchPlaceholder="Search sponsors..."
            disabled={Boolean(sponsorId)}
          />
          <Select
            label="Child"
            value={selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName} · ${selectedChild.code}` : ""}
            onChange={(value) => setSelectedChildId(children.find((child) => `${child.firstName} ${child.lastName} · ${child.code}` === value)?.id ?? "")}
            options={childOptions}
            placeholder="Select a child"
            emptyMessage="No verified or sponsored children available."
            searchable
            searchPlaceholder="Search children..."
            disabled={Boolean(childId)}
          />
          {selectedChild && (
            <p className="rounded-lg bg-cream-100 p-3 text-sm text-ink/60">
              {selectedChild.school} · {selectedChild.classLevel} · {selectedChild.state}
            </p>
          )}
          {selectedChild && !selectedChildIsEligible && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
              This child must be verified before they can be matched with a sponsor.
            </p>
          )}
        </div>
      )}
      <ModalActions>
        <ModalButton onClick={onClose}>Cancel</ModalButton>
        <ModalButton type="button" variant="primary" onClick={() => void handleMatch()} disabled={loading || saving || !selectedSponsorId || !selectedChildId || !selectedChildIsEligible}>
          {saving ? "Matching..." : "Match sponsor"}
        </ModalButton>
      </ModalActions>
    </Modal>
  )
}
